import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { categoriesAPI } from '../../services/api';
import { ArrowLeft, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageUpload from '../../components/ImageUpload';

const CategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const isEditing = Boolean(id);

  // Fetch category data if editing
  const { data: categoryData, isLoading: isLoadingCategory } = useQuery(
    ['category', id],
    () => categoriesAPI.getById(id),
    {
      enabled: isEditing,
      onError: (error) => {
        console.error('Failed to fetch category:', error);
        toast.error('Failed to load category data');
        navigate('/categories');
      }
    }
  );

  const category = categoryData?.data?.data || categoryData?.data;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
    }
  });

  // Set form values when category data is loaded
  useEffect(() => {
    if (category) {
      setValue('name', category.name || '');
      setValue('description', category.description || '');
    }
  }, [category, setValue]);

  const mutation = useMutation(
    async (formData) => {
      if (isEditing) {
        return categoriesAPI.update(id, formData);
      } else {
        return categoriesAPI.create(formData);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditing ? 'Category updated successfully!' : 'Category created successfully!');
        queryClient.invalidateQueries('categories');
        navigate('/categories');
      },
      onError: (error) => {
        console.error('Mutation error:', error);
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  );

  const onSubmit = async (data) => {
    setIsUploading(true);
    try {
      // Validate image is selected for new categories
      if (!selectedImage && !category?.image_url && !isEditing) {
        toast.error('Please select an image');
        setIsUploading(false);
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description || '');
      
      // Add image if selected
      if (selectedImage) {
        formData.append('image', selectedImage);
      }
      
      mutation.mutate(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to save category');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageChange = (file) => {
    setSelectedImage(file);
  };

  const handleCancel = () => {
    navigate('/categories');
  };

  if (isLoadingCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-3 sm:p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                <span className="block sm:inline">{isEditing ? 'Edit Category' : 'Create New Category'}</span>
                {isEditing && category?.name && (
                  <span className="block sm:inline text-base sm:text-lg text-gray-600 dark:text-gray-400 sm:ml-2">
                    : {category.name}
                  </span>
                )}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {isEditing ? 'Update category information' : 'Add a new category to your inventory'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Category Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name', { required: 'Category name is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter category name"
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter category description"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category Image {!isEditing && <span className="text-red-500">*</span>}
              </label>
              <ImageUpload
                value={category?.image_url}
                onChange={handleImageChange}
                label="Category Image"
                required={!isEditing}
                error={!selectedImage && !category?.image_url && !isEditing ? 'Image is required' : ''}
              />
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUploading || mutation.isLoading}
                className="flex-1 sm:flex-none px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {isUploading || mutation.isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEditing ? 'Update Category' : 'Create Category'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoryForm;